import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function ZScoreCalculator() {
  const [value, setValue] = useState("");
  const [mean, setMean] = useState("");
  const [stdDev, setStdDev] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const x = parseFloat(value);
    const mu = parseFloat(mean);
    const sigma = parseFloat(stdDev);

    if (isNaN(x) || isNaN(mu) || isNaN(sigma) || sigma <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers. Standard deviation must be positive.",
        variant: "destructive",
      });
      return;
    }

    const zScore = (x - mu) / sigma;
    setResult(zScore);

    let interpretation = "";
    if (Math.abs(zScore) < 1) {
      interpretation = "Within 1 standard deviation (typical)";
    } else if (Math.abs(zScore) < 2) {
      interpretation = "Within 2 standard deviations (somewhat unusual)";
    } else if (Math.abs(zScore) < 3) {
      interpretation = "Within 3 standard deviations (unusual)";
    } else {
      interpretation = "More than 3 standard deviations (very unusual)";
    }

    toast({
      title: "Z-Score Calculated",
      description: interpretation,
    });
  };

  const reset = () => {
    setValue("");
    setMean("");
    setStdDev("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Z-Score Calculator" 
      calculatorPath="/calculator/z-score"
      category="math"
      subcategory="Statistics"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Z-Score Calculator</CardTitle>
            <CardDescription>
              Calculate standardized score (standard score)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="value">Data Value (x)</Label>
                <Input
                  id="value"
                  type="number"
                  step="any"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Enter data value"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mean">Mean (μ)</Label>
                <Input
                  id="mean"
                  type="number"
                  step="any"
                  value={mean}
                  onChange={(e) => setMean(e.target.value)}
                  placeholder="Enter population mean"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="stdDev">Standard Deviation (σ)</Label>
                <Input
                  id="stdDev"
                  type="number"
                  step="any"
                  value={stdDev}
                  onChange={(e) => setStdDev(e.target.value)}
                  placeholder="Enter standard deviation"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Z-Score</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-primary">{result.toFixed(4)}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {result > 0 ? `${result.toFixed(2)} standard deviations above the mean` : `${Math.abs(result).toFixed(2)} standard deviations below the mean`}
                  </p>
                </CardContent>
              </Card>

              <CalculatorSuperCalcCTA config={calculatorCTAs["z-score"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <MathFormula block>{String.raw`z = \frac{x - \mu}{\sigma}`}</MathFormula>
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  where x is the value, μ is the mean, σ is the standard deviation
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
